"use strict";
/** All user-facing dates use dd.mm.gggg (REPORTING_SPECIFICATION.md §7, DAILY_ENTRY_INTERACTION_SPEC.md §2). */
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatDateSr = formatDateSr;
exports.formatDateObjectSr = formatDateObjectSr;
exports.toIsoDate = toIsoDate;
exports.addLocalDays = addLocalDays;
exports.toPrecedingBusinessDay = toPrecedingBusinessDay;
exports.formatDateTimeSr = formatDateTimeSr;
exports.parseDateSrToIso = parseDateSrToIso;
function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}
/**
 * ISO "YYYY-MM-DD" OR a full ISO datetime string (e.g.
 * "2026-01-01T00:00:00.000Z", the `Date.toISOString()` shape most API DTOs
 * actually transport — see CompanyPerformanceSettingsVersionDto.effectiveFrom,
 * NormDto.validFrom) -> "dd.mm.gggg" (display). Only ever slices the first
 * 10 characters (never constructs a `Date`/re-parses), so a date-only
 * business value never risks a timezone-shifted day.
 */
function formatDateSr(isoDate) {
    const [year, month, day] = isoDate.slice(0, 10).split("-");
    if (!year || !month || !day || day.length !== 2)
        return isoDate;
    return `${day}.${month}.${year}`;
}
/** Date object -> "dd.mm.gggg" (display), using local calendar fields. */
function formatDateObjectSr(date) {
    return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;
}
/** Date object -> "YYYY-MM-DD" (API/DB wire format). */
function toIsoDate(date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}
/**
 * Shifts a Date by `days` local calendar days (e.g. -1 for "yesterday").
 * Built from local `getFullYear`/`getMonth`/`getDate` fields, never from
 * millisecond/UTC arithmetic (`Date.now() - 86400000` drifts by an hour
 * across a DST transition and is banned for this reason — Daily Entry
 * default date, NORMAX_PERFORMANCE_OPERATION_CODE_DAILY_ENTRY_DEFAULT_DATE_FIX_REPORT.md).
 * Passing an out-of-range day (e.g. day 0, or day 32) to the `Date`
 * constructor is standard, well-defined JS behavior: it normalizes across
 * month/year boundaries and leap years automatically, so this needs no
 * manual calendar-math special-casing.
 */
function addLocalDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}
/**
 * If `date` falls on a Saturday or Sunday, rolls it back to the preceding
 * Friday; any other weekday is returned unchanged. Used to keep Daily
 * Entry's default work-date (normally "yesterday" — see `addLocalDays`
 * above) from ever landing on a weekend, since no production happens then —
 * e.g. opening the app on a Monday defaults to the preceding Friday, not
 * Sunday. Built from `getDay()` + `addLocalDays` (local calendar fields, not
 * UTC/millisecond arithmetic), so it inherits the same DST-safety and
 * month/year-boundary correctness.
 */
function toPrecedingBusinessDay(date) {
    const day = date.getDay(); // 0 = Sunday, 6 = Saturday
    if (day === 0)
        return addLocalDays(date, -2);
    if (day === 6)
        return addLocalDays(date, -1);
    return date;
}
/**
 * Full ISO datetime string (e.g. `AuditLog.changedAt.toISOString()`) ->
 * "dd.mm.gggg HH:mm" (display), local wall-clock time. Deliberately DOES
 * construct a `Date` and read local hour/minute — unlike `formatDateSr`,
 * this represents a genuine moment in time (not a pure calendar business
 * date), so converting a UTC instant to the viewer's local wall clock is
 * exactly the correct, non-timezone-confusing behavior here (Daily Entry
 * "Istorija izmena" — NORMAX_PERFORMANCE_DAILY_ENTRY_AUDIT_HISTORY_IMPLEMENTATION_REPORT.md).
 * No seconds — not needed for this use case.
 */
function formatDateTimeSr(isoDateTime) {
    const date = new Date(isoDateTime);
    if (Number.isNaN(date.getTime()))
        return isoDateTime;
    return `${formatDateObjectSr(date)} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}
/** "dd.mm.gggg" -> "YYYY-MM-DD", or null if not parseable as a real calendar date. */
function parseDateSrToIso(input) {
    const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(input.trim());
    if (!match)
        return null;
    const [, day, month, year] = match;
    const d = Number(day);
    const m = Number(month);
    const y = Number(year);
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
        return null;
    }
    return `${year}-${month}-${day}`;
}
//# sourceMappingURL=date.js.map